/**
 * ScriptEditor — the body of a procedure / function / view inner tab.
 *
 * Loads the DROP+CREATE source on mount, lets the user edit it, and saves
 * via POST to the kind-specific endpoint. The save always targets the
 * (connectionId, database, kind) the tab was opened for — same connection +
 * database the source was loaded from, never the "currently selected" one.
 *
 * Props:
 *   connectionId    number
 *   connectionName  string
 *   database        string  — DB the routine lives in
 *   kind            'procedure' | 'function' | 'view'
 *   name            string  — routine name (read-only after open)
 *   active          bool    — true when this inner tab is visible
 *
 * "New routine" mode (created via the object-list ＋ New flow):
 *   isNew           bool    — skip loadRoutine; seed the editor with a skeleton
 *   seedText        string  — the DROP+CREATE skeleton to start from
 *   onCreated       (database, kind, name) => void  — called after the first
 *                             successful save (name parsed from the saved DDL),
 *                             so the host can swap this tab for the real routine.
 */
import { createSignal, createMemo, onMount, onCleanup, Show } from 'solid-js';
import CodeMirrorSQL, { formatEditor } from './CodeMirrorSQL.jsx';
import ColumnExpandModal from './ColumnExpandModal.jsx';
import FormatMenu from './FormatMenu.jsx';
import Modal from './Modal.jsx';
import SPDebugger from './SPDebugger.jsx';
import SchemaRefreshButton from './SchemaRefreshButton.jsx';
import { loadRoutine, saveRoutine, setFunctionDataAccess } from '../lib/routines.js';
import { editorAutoFormat, setEditorAutoFormat, editorLineWrap, setEditorLineWrap } from '../lib/prefs.js';
import { loadCompletionSchema, refreshCompletionSchema, completionState, buildSqlSchema, buildRoutineList } from '../lib/schema.js';
import { toast } from '../lib/toast.js';
import { focusMode, setFocusMode } from '../lib/focusmode.js';

const KIND_LABEL = {
  procedure: 'procedure',
  function:  'function',
  view:      'view',
  event:     'event',
};

export default function ScriptEditor(props) {
  const [text, setText]     = createSignal('');
  const [loading, setLoad]  = createSignal(true);
  const [saving, setSave]   = createSignal(false);
  const [dirty,  setDirty]  = createSignal(false);
  const [error,  setError]  = createSignal('');
  const [expandReq, setExpandReq] = createSignal(null); // Ctrl+Shift+E column expander
  const [accessPrompt, setAccessPrompt] = createSignal(null); // { current } when the data-access confirm is open
  const [applyingAccess, setApplyingAccess] = createSignal(false);
  const [debugOpen, setDebugOpen] = createSignal(false);
  const [createdName, setCreatedName] = createSignal(''); // name parsed from the saved DDL (new mode)
  let view = null;

  // Autocomplete schema for this routine's connection (defaultSchema = its DB).
  onMount(() => { if (props.connectionId) loadCompletionSchema(props.connectionId); });
  const sqlSchema = createMemo(() => {
    if (!props.connectionId) return null;
    const st = completionState(props.connectionId)();
    return {
      schema: buildSqlSchema(st.databases),
      defaultSchema: props.database || undefined,
      routines: buildRoutineList(st.databases, props.database),
    };
  });

  onMount(async () => {
    // New-routine mode: don't load anything — seed the editor with the skeleton.
    if (props.isNew) {
      setText(props.seedText || '');
      setDirty(false);
      setError('');
      setLoad(false);
      return;
    }
    try {
      const r = await loadRoutine(props.connectionId, props.database, props.kind, props.name);
      setText(r.fullScript || '');
      setDirty(false);
      setError('');
    } catch (e) {
      setError(e.message);
      toast.error(`Load ${KIND_LABEL[props.kind]}: ${e.message}`);
    } finally {
      setLoad(false);
    }
  });

  const save = async () => {
    if (saving()) return;
    setSave(true);
    try {
      await saveRoutine(props.connectionId, props.database, props.kind, text());
      setDirty(false);
      // Saving a view/routine changes schema → refresh autocomplete for this DB.
      refreshCompletionSchema(props.connectionId, { database: props.database });

      // ── New-routine mode: parse the ACTUAL name from the saved DDL (the user
      //    may have edited it), then either prompt for a function's data access
      //    or hand off to the host to swap this tab for the real routine. ──
      if (props.isNew) {
        const parsed = parseRoutineName(text());
        if (!parsed) {
          // The routine was created, but we can't determine its name to re-open.
          toast.success('Created. Could not auto-detect the name — find it in the list to keep editing.');
          return; // keep the tab open
        }
        setCreatedName(parsed);
        toast.success(`${capitalize(KIND_LABEL[props.kind])} "${parsed}" created`);
        if (props.kind === 'function') {
          // Run the data-access prompt first; the handoff happens when it closes.
          setAccessPrompt({ current: detectDataAccess(text()) });
        } else {
          props.onCreated?.(props.database, props.kind, parsed);
        }
        return;
      }

      toast.success(`${capitalize(KIND_LABEL[props.kind])} "${props.name}" saved`);
      // For a FUNCTION, confirm whether it modifies data and flag it (drives the
      // read-only-skips-transaction optimization). The CREATE just ran, so the
      // function exists and can be ALTERed. Pre-select whatever the DDL declared.
      if (props.kind === 'function') setAccessPrompt({ current: detectDataAccess(text()) });
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSave(false);
    }
  };

  // The routine name the data-access prompt + flags target: the parsed name in
  // new mode (authoritative), else the tab's name.
  const effectiveName = () => createdName() || props.name;

  // Close the data-access prompt; in new mode this is also the handoff point —
  // swap this "new" tab for the real routine tab.
  const dismissAccessPrompt = () => {
    setAccessPrompt(null);
    if (props.isNew && createdName()) props.onCreated?.(props.database, props.kind, createdName());
  };

  // Apply the data-access choice from the confirm dialog (ALTER FUNCTION), then
  // refresh the classifier metadata for this connection.
  const applyAccess = async (access) => {
    const name = effectiveName();
    setApplyingAccess(true);
    try {
      await setFunctionDataAccess(props.connectionId, props.database, name, access);
      refreshCompletionSchema(props.connectionId, { database: props.database });
      toast.success(`"${name}" flagged ${access === 'MODIFIES SQL DATA' ? 'as modifying data' : 'read-only'}`);
      dismissAccessPrompt(); // closes the prompt + (new mode) hands off to the real tab
    } catch (e) {
      toast.error(`Flag failed: ${e.message}`);
    } finally {
      setApplyingAccess(false);
    }
  };

  // Ctrl+S inside this tab → save.
  const onKey = (e) => {
    if (!props.active) return;
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      save();
    }
  };
  onMount(() => {
    document.addEventListener('keydown', onKey);
    onCleanup(() => document.removeEventListener('keydown', onKey));
  });

  const onChange = (v) => {
    setText(v);
    if (!dirty()) setDirty(true);
  };

  return (
    <div class="script-editor">
      <div class="script-editor-toolbar">
        <span class="muted mono" style="font-size:12px;">
          {props.connectionName} / {props.database} /
        </span>
        <span class="mono strong">{KIND_LABEL[props.kind]} {props.name || `new ${KIND_LABEL[props.kind]}`}</span>
        <Show when={props.isNew}>
          <span class="muted" style="font-size:11px;">(unsaved — Save to create)</span>
        </Show>
        <Show when={dirty()}>
          <span class="script-editor-dirty" title="Unsaved changes">●</span>
        </Show>
        <div class="spacer" />
        <SchemaRefreshButton connId={props.connectionId} database={props.database} />
        <FormatMenu disabled={loading() || !!error()}
                    onPick={(mode) => view && formatEditor(view, mode)}
                    autoFormat={editorAutoFormat()} onToggleAutoFormat={() => setEditorAutoFormat(!editorAutoFormat())}
                    lineWrap={editorLineWrap()} onToggleLineWrap={() => setEditorLineWrap(!editorLineWrap())} />
        <Show when={props.kind === 'procedure' && !props.isNew}>
          <button class="btn btn-sm btn-ghost" onClick={() => setDebugOpen(true)}
                  disabled={loading() || !!error()}
                  title="Step through this procedure with breakpoints + variable watch">
            🐞 Debug
          </button>
        </Show>
        <button class="btn btn-sm" onClick={save}
                disabled={saving() || loading() || !!error()}
                title="Save (Ctrl+S) — runs DROP IF EXISTS then CREATE">
          {saving() ? 'Saving…' : '💾 Save'}
        </button>
        <button class="btn btn-sm btn-ghost" onClick={() => setFocusMode(!focusMode())}
                title={focusMode()
                  ? 'Exit distraction-free mode (Esc)'
                  : 'Distraction-free mode — hide everything but the editor tabs (Esc to exit)'}>
          ⛶ {focusMode() ? 'Exit focus' : 'Focus'}
        </button>
      </div>

      <div class="script-editor-body">
        <Show when={!loading()} fallback={<div class="muted" style="padding:16px;">Loading source…</div>}>
          <Show when={!error()} fallback={
            <div class="muted" style="padding:16px;color:var(--danger,#ef4444);">{error()}</div>
          }>
            <CodeMirrorSQL
              value={text()}
              onInput={onChange}
              onViewReady={(v) => { view = v; }}
              autofocus={props.active}
              autoFormat={editorAutoFormat()}
              lineWrap={editorLineWrap()}
              schema={sqlSchema}
              onExpandColumns={setExpandReq}
              height="100%"
            />
          </Show>
        </Show>
      </div>

      <ColumnExpandModal
        open={!!expandReq()}
        request={expandReq()}
        connId={props.connectionId}
        currentDb={props.database}
        onClose={() => setExpandReq(null)}
      />

      <Show when={debugOpen()}>
        <SPDebugger
          connectionId={props.connectionId}
          database={props.database}
          routine={props.name}
          source={text()}
          onClose={() => setDebugOpen(false)}
        />
      </Show>

      <Show when={accessPrompt()}>
        <Modal open={true} title="Does this function modify data?"
               onClose={dismissAccessPrompt}>
          <p class="muted" style="font-size:13px;margin:0 0 6px;">
            Set the SQL DATA ACCESS for <code class="mono">{effectiveName()}</code>. A read that calls a
            read-only function can run without a transaction; one that modifies data always keeps it.
          </p>
          <Show when={accessPrompt()?.current}>
            <p class="muted" style="font-size:12px;margin:0 0 12px;">
              Currently declared: <code class="mono">{accessPrompt().current}</code>
            </p>
          </Show>
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px;">
            <button class="btn btn-ghost" disabled={applyingAccess()} onClick={dismissAccessPrompt}>
              Leave as is
            </button>
            <button class="btn" disabled={applyingAccess()} onClick={() => applyAccess('READS SQL DATA')}>
              👁 Read-only
            </button>
            <button class="btn btn-danger" disabled={applyingAccess()} onClick={() => applyAccess('MODIFIES SQL DATA')}>
              ✎ Modifies data
            </button>
          </div>
        </Modal>
      </Show>
    </div>
  );
}

function capitalize(s) { return s ? s[0].toUpperCase() + s.slice(1) : s; }

// Parse the routine name from a CREATE PROCEDURE|FUNCTION statement (optionally
// with a DEFINER clause + backtick-quoted name). Authoritative after a new-mode
// save, since the user may have edited the name in the editor. '' when not found.
function parseRoutineName(sql) {
  const m = String(sql || '').match(/CREATE\s+(?:DEFINER\s*=\S+\s+)?(?:PROCEDURE|FUNCTION)\s+`?([A-Za-z_][\w$]*)`?/i);
  return m ? m[1] : '';
}

// Best-effort detection of an existing SQL DATA ACCESS characteristic in the
// routine DDL, to pre-fill the confirm dialog. Rough (not literal-masked) — only
// used as a hint, the user's choice is authoritative.
function detectDataAccess(sql) {
  const m = String(sql || '').match(/\b(MODIFIES SQL DATA|READS SQL DATA|CONTAINS SQL|NO SQL)\b/i);
  return m ? m[1].toUpperCase() : '';
}
